<template>
  <div class="route-making-container" dir="rtl">
    <vue-element-loading :active="isLoading || isSaving" spinner="bar-fade-scale" color="#082C69" />

    <!-- ==================== HEADER & BREADCRUMBS ==================== -->
    <div class="rm-header-row mb-6">
      <div class="rm-header-titles">
        <div class="d-flex align-center gap-2 mb-2">
          <span class="rm-badge-section">تعديل الرحلة</span>
          <div class="rm-breadcrumb">
            <router-link to="/trips" class="rm-bc-link">إدارة الرحلات</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <router-link v-if="routeInfo.id" :to="`/trips/route/${routeInfo.id}`" class="rm-bc-link">{{ routeInfo.name }}</router-link>
            <v-icon x-small color="#94A3B8" class="mx-1">mdi-chevron-left</v-icon>
            <span class="rm-bc-item rm-bc-active">تعديل {{ tripDisplayCode }}</span>
          </div>
        </div>
        <div class="d-flex align-center flex-wrap gap-2">
          <span class="rm-trip-badge rm-badge-lg">{{ tripDisplayCode }}</span>
          <span class="rm-code-badge rm-badge-lg">{{ routeDisplayCode }}</span>
          <h1 class="rm-main-title mb-0">تعديل الرحلة: {{ routeInfo.name || 'جاري التحميل...' }}</h1>
        </div>
        <p class="rm-subtitle mt-1">تحديث جدول أوقات الانطلاق وأيام التشغيل ومعايير التقاط الركاب للرحلة المجدولة</p>
      </div>

      <div class="rm-header-actions d-flex align-center gap-2">
        <v-btn
          depressed
          class="rm-btn-secondary"
          @click="goBack"
          :disabled="isSaving"
        >
          <v-icon right size="18" class="ms-1">mdi-arrow-right</v-icon>
          إلغاء وعودة
        </v-btn>

        <v-btn
          depressed
          class="rm-btn-primary"
          :loading="isSaving"
          @click="submitUpdate"
        >
          <v-icon right size="18" class="ms-1">mdi-check-circle-outline</v-icon>
          حفظ التعديلات
        </v-btn>
      </div>
    </div>

    <!-- ==================== 6 CONTEXT SUMMARY CARDS ==================== -->
    <div class="rm-cards-grid rm-grid-6 mb-6">
      <!-- Card 1: كود الرحلة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">كود الرحلة</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text font-monospace">{{ tripDisplayCode }}</span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">المرجع التشغيلي الثابت</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="22" color="#082C69">mdi-tag-outline</v-icon>
        </div>
      </div>

      <!-- Card 2: المسار التابع له -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسار</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text text-truncate font-weight-bold" style="max-width: 140px;">
              {{ routeInfo.name || '—' }}
            </span>
            <v-skeleton-loader v-else type="text" width="80" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">{{ routeDisplayCode }}</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-green">
          <v-icon size="22" color="#10B981">mdi-road-variant</v-icon>
        </div>
      </div>

      <!-- Card 3: عدد المحطات -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">عدد المحطات</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value">{{ routeInfo.stops_count || stops.length || 0 }}</span>
            <v-skeleton-loader v-else type="text" width="40" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">محطة مسار معتمدة</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-amber">
          <v-icon size="22" color="#F59E0B">mdi-bus-stop</v-icon>
        </div>
      </div>

      <!-- Card 4: المسافة الكلية -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المسافة الكلية</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text font-weight-bold">
              {{ formattedDistance }}
            </span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">مسار الطرق المعتمد</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-purple">
          <v-icon size="22" color="#8B5CF6">mdi-map-marker-distance</v-icon>
        </div>
      </div>

      <!-- Card 5: المدة التقريبية -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">المدة التقريبية</span>
          <div class="rm-stat-val-wrapper">
            <span v-if="!isLoading" class="rm-stat-value-text font-weight-bold text-navy">
              {{ calculatedDuration || tripInfo.expected_duration || routeInfo.canonical_duration || '—' }}
            </span>
            <v-skeleton-loader v-else type="text" width="70" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">توقيت حقيقي للمحطات</span>
        </div>
        <div class="rm-stat-icon-box rm-icon-blue">
          <v-icon size="22" color="#082C69">mdi-clock-time-four-outline</v-icon>
        </div>
      </div>

      <!-- Card 6: حالة الرحلة -->
      <div class="rm-stat-card">
        <div class="rm-stat-content">
          <span class="rm-stat-label">حالة الرحلة</span>
          <div class="rm-stat-val-wrapper">
            <span
              v-if="!isLoading"
              class="rm-status-badge mt-1"
              :class="tripInfo.status_id === 1 ? 'status-active' : 'status-paused'"
            >
              <span class="rm-status-dot"></span>
              {{ tripInfo.status_name || (tripInfo.status_id === 1 ? 'مجدولة' : 'متوقفة مؤقتًا') }}
            </span>
            <v-skeleton-loader v-else type="text" width="60" class="my-1"></v-skeleton-loader>
          </div>
          <span class="rm-stat-caption">متاحة للجدولة والتشغيل</span>
        </div>
        <div class="rm-stat-icon-box" :class="tripInfo.status_id === 1 ? 'rm-icon-green' : 'rm-icon-amber'">
          <v-icon size="22" :color="tripInfo.status_id === 1 ? '#10B981' : '#F59E0B'">mdi-calendar-check</v-icon>
        </div>
      </div>
    </div>

    <!-- ==================== 2-COLUMN MAIN LAYOUT ==================== -->
    <div class="rm-two-col-layout">
      <!-- ==================== RIGHT COLUMN (52%): EDITABLE SETTINGS ==================== -->
      <div class="rm-col-form">
        <!-- Section 1: إعدادات المواعيد والتشغيل -->
        <div class="rm-card mb-6">
          <div class="rm-card-header d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-clock-edit-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">إعدادات مواعيد وتشغيل الرحلة</h2>
                <p class="rm-card-subtitle">تعديل توقيت الانطلاق من أول محطة والأيام المجدولة للتشغيل</p>
              </div>
            </div>
          </div>

          <div class="rm-card-body">
            <!-- Departure Time Field -->
            <div class="mb-5">
              <label class="rm-input-label">
                <v-icon x-small color="#082C69" class="me-1">mdi-clock-start</v-icon>
                وقت الانطلاق من أول محطة <span class="text-danger">*</span>
              </label>
              <div class="d-flex align-center gap-3">
                <input
                  type="time"
                  v-model="form.first_stop_time"
                  class="rm-custom-time-input"
                  required
                  @change="onTimingParamChange"
                />
                <span class="rm-time-hint">
                  يتم احتساب مواعيد وصول المحطات (ETAs) تلقائيًا بناءً على أزمنة المسار الحقيقية
                </span>
              </div>
            </div>

            <!-- Operating Days Field -->
            <div class="mb-5">
              <div class="d-flex align-center justify-space-between mb-2">
                <label class="rm-input-label mb-0">
                  <v-icon x-small color="#082C69" class="me-1">mdi-calendar-week</v-icon>
                  أيام التشغيل الأسبوعية <span class="text-danger">*</span>
                </label>
                <div class="d-flex align-center gap-2">
                  <button
                    type="button"
                    class="rm-link-btn"
                    @click="selectAllDays"
                  >
                    كل الأيام
                  </button>
                  <span class="text--disabled">|</span>
                  <button
                    type="button"
                    class="rm-link-btn"
                    @click="selectWorkDays"
                  >
                    أيام العمل (الأحد - الخميس)
                  </button>
                  <span class="text--disabled">|</span>
                  <button
                    type="button"
                    class="rm-link-btn text-danger"
                    @click="clearDays"
                  >
                    إلغاء التحديد
                  </button>
                </div>
              </div>

              <!-- Day Pills Selector -->
              <div class="rm-days-selector-grid">
                <button
                  type="button"
                  v-for="day in allWeekdays"
                  :key="day"
                  class="rm-day-pill"
                  :class="{ 'day-selected': isDaySelected(day) }"
                  @click="toggleDay(day)"
                >
                  <v-icon x-small :color="isDaySelected(day) ? '#FFFFFF' : '#64748B'" class="me-1">
                    {{ isDaySelected(day) ? 'mdi-check-circle' : 'mdi-circle-outline' }}
                  </v-icon>
                  {{ day }}
                </button>
              </div>
              <p v-if="form.operating_days.length === 0" class="text-caption text-danger mt-1">
                يجب اختيار يوم تشغيل واحد على الأقل للرحلة
              </p>
            </div>

            <!-- Wait Time & Effective Date Grid (No manual travel time) -->
            <div class="rm-grid-2-fields mb-2">
              <div>
                <label class="rm-input-label">
                  <v-icon x-small color="#082C69" class="me-1">mdi-timer-sand</v-icon>
                  وقت الانتظار بالمحطة (بالدقائق)
                </label>
                <input
                  type="number"
                  v-model.number="form.station_wait_minutes"
                  min="0"
                  max="60"
                  class="rm-custom-input"
                  placeholder="5"
                  @input="onTimingParamChange"
                />
                <span class="rm-field-helper">المدة المقدرة لتوقف الحافلة وصعود الركاب عند كل محطة</span>
              </div>

              <div>
                <label class="rm-input-label">
                  <v-icon x-small color="#082C69" class="me-1">mdi-calendar-start</v-icon>
                  تاريخ سريان الرحلة
                </label>
                <input
                  type="date"
                  v-model="form.effective_date"
                  class="rm-custom-input"
                  @change="onTimingParamChange"
                />
                <span class="rm-field-helper">تاريخ بدء العمل بجدول هذه الرحلة</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Section 2: خيارات الاشتراك وحجز المقاعد -->
        <div class="rm-card">
          <div class="rm-card-header d-flex align-center mb-4">
            <div class="rm-icon-header-box me-3">
              <v-icon size="20" color="#082C69">mdi-card-account-details-outline</v-icon>
            </div>
            <div>
              <h2 class="rm-card-title">خيارات الاشتراك التجاري</h2>
              <p class="rm-card-subtitle">تحديد أهلية مقاعد الرحلة لباقات واشتراكات الركاب</p>
            </div>
          </div>

          <div class="rm-card-body">
            <div class="d-flex flex-column gap-3">
              <!-- Toggle 1: Available for Subscription -->
              <div
                class="rm-toggle-row"
                :class="{ 'is-active': form.available_for_subscription }"
                @click="toggleAvailableForSubscription"
              >
                <div class="rm-toggle-switch">
                  <div class="rm-toggle-track">
                    <div class="rm-toggle-thumb"></div>
                  </div>
                </div>
                <div class="rm-toggle-text">
                  <span class="rm-toggle-title">متاحة لاشتراكات الركاب</span>
                  <span class="rm-toggle-desc">السماح لعملاء الباقات بحجز مقاعد مجدولة على متن هذه الرحلة</span>
                </div>
              </div>

              <!-- Toggle 2: Subscription Only -->
              <div
                class="rm-toggle-row"
                :class="{
                  'is-active': form.subscription_only,
                  'is-disabled': !form.available_for_subscription
                }"
                @click="toggleSubscriptionOnly"
              >
                <div class="rm-toggle-switch">
                  <div class="rm-toggle-track">
                    <div class="rm-toggle-thumb"></div>
                  </div>
                </div>
                <div class="rm-toggle-text">
                  <span class="rm-toggle-title">حصرية للمشتركين فقط</span>
                  <span class="rm-toggle-desc">حظر الحجوزات الفردية العادية وقصر الرحلة كلياً على المشتركين</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ==================== LEFT COLUMN (48%): ROUTE MAP & LIVE TIMETABLE ==================== -->
      <div class="rm-col-preview">
        <!-- Live Timing Status Banner -->
        <div class="rm-live-banner mb-4">
          <div class="d-flex align-center">
            <div class="rm-pulse-dot me-3"></div>
            <div>
              <span class="font-weight-bold d-block text-navy">معاينة حية للمسار ومواعيد المحطات</span>
              <span class="text-caption text--secondary">
                الوصول المتوقع للمحطة الأخيرة:
                <strong class="text-navy font-weight-bold">{{ calculatedLastStopTimeDisplay }}</strong>
              </span>
            </div>
          </div>
          <v-chip x-small color="#082C69" dark class="font-weight-bold">
            {{ isRecalculating ? 'جاري الحساب...' : 'محدث تلقائيًا' }}
          </v-chip>
        </div>

        <!-- Google Map Preview Card (Visual family matching View Trip) -->
        <div class="rm-card rm-map-card mb-4">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-map-search-outline</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">خريطة مسار الرحلة</h2>
                <p class="rm-card-subtitle">المسار الجغرافي ونقاط المحطات المعتمدة للرحلة</p>
              </div>
            </div>

            <v-btn
              depressed
              small
              class="rm-btn-secondary"
              @click="fitMapBounds"
            >
              <v-icon size="16" class="me-1">mdi-fit-to-screen-outline</v-icon>
              إظهار المسار بالكامل
            </v-btn>
          </div>

          <!-- Map Component -->
          <div class="rm-map-view-wrapper">
            <GoogleMapLoader
              ref="mapLoader"
              :enabled="false"
              :center="center"
              :selected="selectedItem"
              :zoom="zoom"
              :apiKey="apiKey"
              :markers="markers"
              :polylines="polyline"
              :mapOptions="mapOptions"
            />
          </div>

          <!-- Map Legend -->
          <div class="rm-map-footer-legend d-flex align-center flex-wrap gap-4 mt-3 pt-2 border-top">
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-start"></span>
              نقطة البداية ({{ stops.length > 0 ? stops[0].name : 'المحطة الأولى' }})
            </span>
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-end"></span>
              نقطة النهاية ({{ stops.length > 1 ? stops[stops.length - 1].name : 'المحطة الأخيرة' }})
            </span>
            <span class="rm-legend-item">
              <span class="rm-legend-dot rm-dot-intermediate"></span>
              محطات التوقف الوسيطة
            </span>
          </div>
        </div>

        <!-- Live Stops Timeline Card -->
        <div class="rm-card">
          <div class="rm-card-header d-flex align-center justify-space-between mb-3">
            <div class="d-flex align-center">
              <div class="rm-icon-header-box me-3">
                <v-icon size="20" color="#082C69">mdi-format-list-numbered-rtl</v-icon>
              </div>
              <div>
                <h2 class="rm-card-title">جدول مواعيد محطات الرحلة</h2>
                <p class="rm-card-subtitle">توقيت الوصول المقدر (ETA) لكل محطة تلقائياً</p>
              </div>
            </div>
            <span class="rm-table-count">{{ stops.length }} محطة</span>
          </div>

          <div class="rm-stops-scroll-wrapper">
            <div
              v-for="(stop, index) in stops"
              :key="stop.stop_id || stop.id || index"
              class="rm-stop-timeline-item"
              :class="{ 'item-selected': selectedStopIdx === index }"
              @click="selectStop(stop, index)"
            >
              <!-- Order Indicator Column -->
              <div class="rm-stop-order-col">
                <span
                  class="rm-order-circle"
                  :class="{
                    'circle-first': index === 0,
                    'circle-last': index === stops.length - 1 && stops.length > 1,
                    'circle-mid': index > 0 && index < stops.length - 1,
                  }"
                >
                  {{ index + 1 }}
                </span>
                <div v-if="index < stops.length - 1" class="rm-timeline-line"></div>
              </div>

              <!-- Stop Information Column -->
              <div class="rm-stop-info-col flex-grow-1">
                <div class="d-flex align-center justify-space-between mb-1">
                  <span class="font-weight-bold text-navy">{{ stop.name }}</span>
                  <!-- ETA Pill -->
                  <span class="rm-eta-pill">
                    <v-icon x-small color="#082C69" class="me-1">mdi-clock-outline</v-icon>
                    {{ formatTime(stop.eta) }}
                  </span>
                </div>

                <div class="d-flex align-center justify-space-between text-caption text--secondary">
                  <span class="text-truncate" style="max-width: 200px;">{{ stop.address || '—' }}</span>
                  <span v-if="index > 0 && (stop.road_minutes_from_previous || stop.road_minutes)" class="rm-road-time-tag">
                    +{{ stop.road_minutes_from_previous || stop.road_minutes }} دقيقة طريق
                  </span>
                  <span v-else-if="index === 0" class="rm-start-tag">
                    نقطة الانطلاق الأولى
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueElementLoading from "vue-element-loading";
import GoogleMapLoader, { BEEPGO_MAP_STYLE } from "@/components/GoogleMapLoader";
import { Keys } from "/src/config.js";

export default {
  name: "RouteMakingEditTrip",
  components: {
    VueElementLoading,
    GoogleMapLoader,
  },
  props: {
    tripId: {
      type: [Number, String],
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      isRecalculating: false,
      apiKey: Keys.GOOGLE_MAPS_API_KEY || "",
      tripInfo: {},
      routeInfo: {},
      stops: [],
      polyline: [],
      markers: [],
      center: { lat: 30.0444, lng: 31.2357 },
      zoom: 12,
      selectedItem: null,
      selectedStopIdx: 0,
      debounceTimer: null,
      calculatedDuration: null,
      calculatedLastStopTime: null,
      mapOptions: {
        styles: BEEPGO_MAP_STYLE,
        disableDefaultUI: false,
        zoomControl: true,
        fullscreenControl: true,
        mapTypeControl: false,
        streetViewControl: false,
      },
      allWeekdays: [
        "السبت",
        "الأحد",
        "الاثنين",
        "الثلاثاء",
        "الأربعاء",
        "الخميس",
        "الجمعة",
      ],
      form: {
        first_stop_time: "08:00",
        operating_days: ["السبت", "الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"],
        station_wait_minutes: 5,
        effective_date: new Date().toISOString().slice(0, 10),
        available_for_subscription: true,
        subscription_only: false,
      },
    };
  },
  computed: {
    effectiveTripId() {
      if (this.tripId) return this.tripId;
      const routeParam = this.$route.params.trip_id || this.$route.query.trip_id;
      if (routeParam) return routeParam;
      const fullPath = this.$route.fullPath || "";
      const match = fullPath.match(/trip=(\d+)/);
      return match ? match[1] : null;
    },
    tripDisplayCode() {
      if (this.tripInfo && this.tripInfo.trip_reference) {
        return this.tripInfo.trip_reference;
      }
      if (this.effectiveTripId) {
        return `T-${String(this.effectiveTripId).padStart(4, "0")}`;
      }
      return "—";
    },
    routeDisplayCode() {
      if (this.routeInfo && this.routeInfo.route_code) {
        return this.routeInfo.route_code;
      }
      if (this.routeInfo && this.routeInfo.id) {
        return `R-${String(this.routeInfo.id).padStart(4, "0")}`;
      }
      return "—";
    },
    formattedDistance() {
      const dist = this.routeInfo.canonical_distance;
      if (!dist) return "—";
      const s = String(dist);
      return s.includes("كم") ? s : `${s} كم`;
    },
    calculatedLastStopTimeDisplay() {
      const t = this.calculatedLastStopTime || this.tripInfo.last_stop_time;
      if (!t) return "—";
      return this.formatTime(t);
    },
  },
  created() {
    if (this.effectiveTripId) {
      this.fetchTripDetails();
    } else {
      this.notifyUser("error", "خطأ", "معرف الرحلة غير محدد");
      this.$router.push("/trips");
    }
  },
  methods: {
    notifyUser(type, title, text) {
      if (this.$notify) {
        this.$notify({
          title: title || (type === "success" ? "تم بنجاح" : type === "warn" ? "تنبيه" : "خطأ"),
          text: text,
          type: type === "warn" ? "warn" : type,
        });
      } else if (this.$swal) {
        this.$swal(
          title || (type === "success" ? "تم بنجاح" : "خطأ"),
          text,
          type === "warn" ? "warning" : type
        );
      } else {
        alert(text);
      }
    },
    fetchTripDetails() {
      this.isLoading = true;
      axios
        .get(`/trips/${this.effectiveTripId}/parent-details`)
        .then((response) => {
          if (response.data) {
            this.tripInfo = response.data.trip || {};
            this.routeInfo = response.data.route || {};
            this.stops = response.data.stops || [];

            // Populate form
            this.form.first_stop_time = this.cleanTimeStr(this.tripInfo.first_stop_time) || "08:00";
            this.form.station_wait_minutes = this.tripInfo.station_wait_minutes ?? 5;
            this.form.effective_date = this.tripInfo.effective_date || new Date().toISOString().slice(0, 10);
            this.form.available_for_subscription = Boolean(this.tripInfo.available_for_subscription);
            this.form.subscription_only = Boolean(this.tripInfo.subscription_only);

            // Parse operating days
            this.form.operating_days = this.parseOperatingDays(this.tripInfo);

            // Map polyline & markers
            this.buildMapLayers(response.data.map, this.stops);

            this.calculatedDuration = this.tripInfo.expected_duration || this.routeInfo.canonical_duration;
            this.calculatedLastStopTime = this.tripInfo.last_stop_time;
          }
        })
        .catch((error) => {
          console.error("Error fetching trip parent details:", error);
          this.notifyUser("error", "خطأ", "تعذر تحميل بيانات الرحلة المحددة");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    parseOperatingDays(trip) {
      let op = trip.operating_days_raw || trip.operating_days;
      if (Array.isArray(op) && op.length > 0) return op;

      if (typeof op === "string") {
        try {
          const parsed = JSON.parse(op);
          if (Array.isArray(parsed) && parsed.length > 0) return parsed;
        } catch (e) {
          if (op.includes(",")) {
            const split = op.split(",").map((s) => s.trim()).filter(Boolean);
            if (split.length > 0) return split;
          }
        }

        if (op.includes("الأحد") && op.includes("الخميس")) {
          return ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
        }
        if (op === "يوميًا" || op === "يومياً") {
          return [...this.allWeekdays];
        }
      }

      if (trip.repetition_period === 1 || !op) {
        return [...this.allWeekdays];
      }

      return ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
    },
    buildMapLayers(mapData, stopsList) {
      const stops = stopsList || [];
      const total = stops.length;

      // 1. Build distinct markers
      this.markers = stops.map((stop, idx) => {
        const isStart = idx === 0;
        const isEnd = idx === total - 1 && total > 1;
        let iconUrl = "https://maps.google.com/mapfiles/ms/icons/blue-dot.png";
        if (isStart) {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/green-dot.png";
        } else if (isEnd) {
          iconUrl = "https://maps.google.com/mapfiles/ms/icons/red-dot.png";
        }

        return {
          id: stop.stop_id || stop.id,
          place_id: String(stop.stop_id || stop.id),
          position: {
            lat: Number(stop.lat),
            lng: Number(stop.lng),
          },
          title: stop.name,
          order: idx + 1,
          type: isStart ? "start" : isEnd ? "end" : "stop",
          icon: iconUrl,
          infoText: `<strong>#${idx + 1} - ${stop.name}</strong><br/>${stop.address || ""}<br/>ETA: ${this.formatTime(stop.eta)}`,
          zIndex: isStart || isEnd ? 10 : 2,
        };
      });

      // 2. Build Polyline
      if (mapData && mapData.polyline && mapData.polyline.length > 0) {
        this.polyline = [
          {
            data: mapData.polyline,
            path: mapData.polyline,
            strokeColor: "#082C69",
            strokeWeight: 5,
            strokeOpacity: 0.9,
          },
        ];
      } else {
        this.polyline = [];
      }

      // 3. Center map
      if (this.markers.length > 0 && this.markers[0].position) {
        this.center = this.markers[0].position;
      } else if (this.polyline.length > 0 && this.polyline[0].data && this.polyline[0].data.length > 0) {
        this.center = this.polyline[0].data[0];
      }

      this.$nextTick(() => {
        setTimeout(() => {
          this.fitMapBounds();
        }, 300);
      });
    },
    fitMapBounds() {
      if (this.$refs.mapLoader && typeof this.$refs.mapLoader.fitBounds === "function") {
        this.$refs.mapLoader.fitBounds();
      } else if (this.markers.length > 0 && this.markers[0].position) {
        this.center = this.markers[0].position;
        this.zoom = 12;
      }
    },
    cleanTimeStr(timeStr) {
      if (!timeStr) return "";
      const parts = timeStr.split(":");
      if (parts.length >= 2) {
        return `${parts[0].padStart(2, "0")}:${parts[1].padStart(2, "0")}`;
      }
      return timeStr;
    },
    formatTime(timeStr) {
      if (!timeStr) return "—";
      const parts = timeStr.split(":");
      if (parts.length < 2) return timeStr;
      let hours = parseInt(parts[0], 10);
      const minutes = parts[1];
      const ampm = hours >= 12 ? "م" : "ص";
      hours = hours % 12;
      hours = hours ? hours : 12;
      return `${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
    },
    isDaySelected(day) {
      return this.form.operating_days.includes(day);
    },
    toggleDay(day) {
      const idx = this.form.operating_days.indexOf(day);
      if (idx > -1) {
        this.form.operating_days.splice(idx, 1);
      } else {
        this.form.operating_days.push(day);
      }
      this.onTimingParamChange();
    },
    selectAllDays() {
      this.form.operating_days = [...this.allWeekdays];
      this.onTimingParamChange();
    },
    selectWorkDays() {
      this.form.operating_days = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس"];
      this.onTimingParamChange();
    },
    clearDays() {
      this.form.operating_days = [];
      this.onTimingParamChange();
    },
    toggleAvailableForSubscription() {
      this.form.available_for_subscription = !this.form.available_for_subscription;
      if (!this.form.available_for_subscription) {
        this.form.subscription_only = false;
      }
    },
    toggleSubscriptionOnly() {
      if (!this.form.available_for_subscription) return;
      this.form.subscription_only = !this.form.subscription_only;
    },
    onTimingParamChange() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.recalculateTimingPreview();
      }, 350);
    },
    recalculateTimingPreview() {
      if (!this.routeInfo.id || !this.form.first_stop_time) return;

      this.isRecalculating = true;
      axios
        .post("/trips/timing-preview", {
          route_id: this.routeInfo.id,
          first_stop_time: this.form.first_stop_time,
          effective_date: this.form.effective_date,
          station_wait_minutes: this.form.station_wait_minutes,
        })
        .then((res) => {
          const timingData = res.data && res.data.timing ? res.data.timing : res.data;
          const previewStops = (timingData && timingData.stops) || [];

          if (Array.isArray(previewStops) && previewStops.length > 0) {
            // Update stop ETAs with real calculated times
            this.stops = this.stops.map((stop, i) => {
              const matched =
                previewStops.find((ps) => Number(ps.stop_id) === Number(stop.stop_id || stop.id)) ||
                previewStops[i];
              if (matched) {
                const arrivalStr = matched.arrival_time_str || (matched.arrival_at ? matched.arrival_at.split(" ")[1] : null);
                return {
                  ...stop,
                  eta: arrivalStr || stop.eta,
                  road_minutes: matched.road_minutes_from_previous ?? stop.road_minutes,
                  road_minutes_from_previous: matched.road_minutes_from_previous ?? stop.road_minutes_from_previous,
                };
              }
              return stop;
            });

            // Update marker popups
            this.markers = this.markers.map((marker, idx) => {
              const stop = this.stops[idx];
              if (stop) {
                return {
                  ...marker,
                  infoText: `<strong>#${idx + 1} - ${stop.name}</strong><br/>${stop.address || ""}<br/>ETA: ${this.formatTime(stop.eta)}`,
                };
              }
              return marker;
            });

            if (timingData.expected_end_at) {
              const parts = timingData.expected_end_at.split(" ");
              this.calculatedLastStopTime = parts[1] || timingData.expected_end_at;
            } else if (previewStops.length > 0) {
              const last = previewStops[previewStops.length - 1];
              this.calculatedLastStopTime = last.arrival_time_str;
            }

            if (timingData.total_trip_minutes) {
              this.calculatedDuration = `${timingData.total_trip_minutes} دقيقة`;
            }
          }
        })
        .catch((err) => {
          console.error("Failed to recalculate preview:", err);
        })
        .finally(() => {
          this.isRecalculating = false;
        });
    },
    selectStop(stop, index) {
      this.selectedStopIdx = index;
      this.selectedItem = String(stop.stop_id || stop.id);
      if (stop.lat && stop.lng) {
        this.center = { lat: Number(stop.lat), lng: Number(stop.lng) };
        this.zoom = 15;
      }
    },
    submitUpdate() {
      if (!this.form.first_stop_time) {
        this.notifyUser("warn", "تنبيه", "يرجى تحديد وقت الانطلاق من أول محطة");
        return;
      }
      if (!this.form.operating_days || this.form.operating_days.length === 0) {
        this.notifyUser("warn", "تنبيه", "يرجى اختيار يوم تشغيل واحد على الأقل للرحلة");
        return;
      }

      this.isSaving = true;

      const payload = {
        action: "edit",
        trip: {
          id: Number(this.effectiveTripId),
          route_id: Number(this.routeInfo.id || this.tripInfo.route_id),
          first_stop_time: this.form.first_stop_time,
          effective_date: this.form.effective_date,
          repetition_period: this.tripInfo.repetition_period || 1,
          station_wait_minutes: Number(this.form.station_wait_minutes) || 0,
          available_for_subscription: Boolean(this.form.available_for_subscription),
          subscription_only: Boolean(this.form.subscription_only),
          operating_days: this.form.operating_days,
        },
      };

      axios
        .post("/trips/create-edit", payload)
        .then((response) => {
          this.notifyUser("success", "تم بنجاح", "تم حفظ وتحديث بيانات الرحلة بنجاح");
          const targetRouteId = this.routeInfo.id || this.tripInfo.route_id;
          setTimeout(() => {
            if (targetRouteId) {
              this.$router.push(`/trips/route/${targetRouteId}`);
            } else {
              this.$router.push("/trips");
            }
          }, 450);
        })
        .catch((error) => {
          console.error("Error saving trip edits:", error);
          let msg = "تعذر حفظ تعديلات الرحلة، يرجى المحاولة لاحقاً";
          if (error.response && error.response.status === 422) {
            msg =
              error.response.data.message ||
              (error.response.data.errors
                ? Object.values(error.response.data.errors).flat().join(" ")
                : null) ||
              "توجد تعارضات أو بيانات غير صالحة";
          } else if (error.response && error.response.status === 500) {
            msg = error.response.data.message || "حدث خطأ غير متوقع في الخادم أثناء الحفظ";
          }
          this.notifyUser("error", "تعذر الحفظ", msg);
        })
        .finally(() => {
          this.isSaving = false;
        });
    },
    goBack() {
      if (this.routeInfo.id) {
        this.$router.push(`/trips/route/${this.routeInfo.id}`);
      } else {
        this.$router.push("/trips");
      }
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap");

.route-making-container {
  font-family: "Cairo", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  direction: rtl;
  text-align: right;
  padding: 16px 20px 40px 20px;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Header & Breadcrumbs */
.rm-header-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-badge-section {
  display: inline-block;
  background: rgba(8, 44, 105, 0.08);
  color: #082c69;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-breadcrumb {
  display: flex;
  align-items: center;
  font-size: 12px;
}

.rm-bc-link {
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.rm-bc-link:hover {
  color: #082c69;
}

.rm-bc-item.rm-bc-active {
  color: #082c69;
  font-weight: 700;
}

.rm-main-title {
  font-size: 22px;
  font-weight: 800;
  color: #082c69;
  line-height: 1.3;
}

.rm-subtitle {
  color: #64748b;
  font-size: 13px;
  margin: 0;
}

.rm-code-badge {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #475569;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  border: 1px solid #e2e8f0;
}

.rm-trip-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(8, 44, 105, 0.1);
  color: #082c69;
  font-weight: 800;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  letter-spacing: 0.5px;
}

.rm-badge-lg {
  font-size: 13px;
  padding: 5px 12px;
}

/* Action Buttons */
.rm-btn-primary {
  background: #082c69 !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  padding: 0 18px !important;
  height: 40px !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(8, 44, 105, 0.25) !important;
  transition: all 0.2s ease !important;
}

.rm-btn-primary:hover {
  background: #061b40 !important;
  transform: translateY(-1px);
}

.rm-btn-secondary {
  background: #ffffff !important;
  color: #475569 !important;
  border: 1px solid #cbd5e1 !important;
  font-weight: 700 !important;
  border-radius: 8px !important;
  padding: 0 16px !important;
  height: 40px !important;
  text-transform: none !important;
  transition: all 0.2s ease !important;
}

.rm-btn-secondary:hover {
  background: #f8fafc !important;
  border-color: #94a3b8 !important;
  color: #082c69 !important;
}

/* Summary Grid */
.rm-cards-grid {
  display: grid;
  gap: 14px;
}

.rm-grid-6 {
  grid-template-columns: repeat(6, 1fr);
}

@media (max-width: 1300px) {
  .rm-grid-6 {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .rm-grid-6 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 480px) {
  .rm-grid-6 {
    grid-template-columns: 1fr;
  }
}

.rm-stat-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.rm-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}

.rm-stat-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 2px;
}

.rm-stat-value {
  font-size: 18px;
  font-weight: 800;
  color: #082c69;
}

.rm-stat-value-text {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
}

.rm-stat-caption {
  display: block;
  font-size: 10px;
  color: #94a3b8;
  margin-top: 2px;
}

.rm-stat-icon-box {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rm-icon-blue {
  background: rgba(8, 44, 105, 0.08);
}
.rm-icon-green {
  background: rgba(16, 185, 129, 0.1);
}
.rm-icon-amber {
  background: rgba(245, 158, 11, 0.1);
}
.rm-icon-purple {
  background: rgba(139, 92, 246, 0.1);
}

.rm-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
}

.status-active {
  background: #ecfdf5;
  color: #059669;
}

.status-paused {
  background: #fffbeb;
  color: #d97706;
}

.rm-status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* 2-Column Layout */
.rm-two-col-layout {
  display: grid;
  grid-template-columns: 52% 48%;
  gap: 20px;
  align-items: start;
}

@media (max-width: 1024px) {
  .rm-two-col-layout {
    grid-template-columns: 1fr;
  }
}

/* Card Styling */
.rm-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rm-icon-header-box {
  width: 36px;
  height: 36px;
  background: rgba(8, 44, 105, 0.08);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.rm-card-title {
  font-size: 15px;
  font-weight: 700;
  color: #082c69;
  margin: 0;
}

.rm-card-subtitle {
  font-size: 12px;
  color: #64748b;
  margin: 0;
}

.rm-table-count {
  font-size: 12px;
  color: #64748b;
  font-weight: 600;
  background: #f8fafc;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

/* Inputs & Form */
.rm-input-label {
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.rm-custom-input,
.rm-custom-time-input {
  width: 100%;
  height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  background-color: #ffffff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.rm-custom-time-input {
  max-width: 180px;
  font-weight: 700;
  font-size: 16px;
  color: #082c69;
  text-align: center;
}

.rm-custom-input:focus,
.rm-custom-time-input:focus {
  border-color: #082c69;
  box-shadow: 0 0 0 3px rgba(8, 44, 105, 0.1);
}

.rm-time-hint {
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
}

.rm-field-helper {
  display: block;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
}

.rm-grid-2-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

@media (max-width: 600px) {
  .rm-grid-2-fields {
    grid-template-columns: 1fr;
  }
}

.rm-link-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: #082c69;
  cursor: pointer;
  text-decoration: underline;
}

.rm-link-btn:hover {
  color: #1e40af;
}

/* Day Pills Grid */
.rm-days-selector-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.rm-day-pill {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s ease;
}

.rm-day-pill:hover {
  border-color: #082c69;
  color: #082c69;
}

.rm-day-pill.day-selected {
  background: #082c69;
  color: #ffffff;
  border-color: #082c69;
  box-shadow: 0 2px 4px rgba(8, 44, 105, 0.2);
}

/* Custom Subscription Toggle Switches */
.rm-toggle-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
}

.rm-toggle-row:hover:not(.is-disabled) {
  border-color: #cbd5e1;
  background: #f8fafc;
}

.rm-toggle-row.is-active {
  border-color: #bfdbfe;
  background: #f8faff;
}

.rm-toggle-row.is-disabled {
  opacity: 0.55;
  cursor: not-allowed;
  background: #f1f5f9;
}

.rm-toggle-switch {
  flex-shrink: 0;
}

.rm-toggle-track {
  width: 44px;
  height: 24px;
  border-radius: 12px;
  background: #cbd5e1;
  position: relative;
  transition: background 0.25s ease;
}

.rm-toggle-row.is-active .rm-toggle-track {
  background: #082c69;
}

.rm-toggle-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  position: absolute;
  top: 3px;
  right: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.rm-toggle-row.is-active .rm-toggle-thumb {
  transform: translateX(-20px);
}

.rm-toggle-text {
  display: flex;
  flex-direction: column;
}

.rm-toggle-title {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}

.rm-toggle-desc {
  font-size: 0.775rem;
  color: #64748b;
  line-height: 1.4;
  margin-top: 2px;
}

/* Left Column Live Preview */
.rm-live-banner {
  background: #ffffff;
  border: 1px solid rgba(8, 44, 105, 0.15);
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

.rm-pulse-dot {
  width: 10px;
  height: 10px;
  background-color: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulse-green 1.8s infinite;
}

@keyframes pulse-green {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

/* Map Wrapper */
.rm-map-view-wrapper {
  height: 440px;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  position: relative;
}

.rm-map-footer-legend {
  font-size: 0.775rem;
  color: #64748b;
}

.rm-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rm-legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.rm-dot-start {
  background: #10b981;
}
.rm-dot-end {
  background: #ef4444;
}
.rm-dot-intermediate {
  background: #082c69;
}

/* Stops Timeline List */
.rm-stops-scroll-wrapper {
  max-height: 440px;
  overflow-y: auto;
  padding-left: 6px;
}

.rm-stop-timeline-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.rm-stop-timeline-item:hover {
  background-color: #f1f5f9;
}

.rm-stop-timeline-item.item-selected {
  background-color: rgba(8, 44, 105, 0.06);
}

.rm-stop-order-col {
  width: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  margin-left: 12px;
}

.rm-order-circle {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #ffffff;
}

.circle-first {
  background-color: #10b981;
}

.circle-last {
  background-color: #ef4444;
}

.circle-mid {
  background-color: #082c69;
}

.rm-timeline-line {
  width: 2px;
  height: 34px;
  background-color: #cbd5e1;
  margin-top: 4px;
}

.rm-eta-pill {
  background: rgba(8, 44, 105, 0.08);
  color: #082c69;
  font-size: 12px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
}

.rm-road-time-tag {
  color: #0284c7;
  font-weight: 600;
  font-size: 11px;
}

.rm-start-tag {
  color: #10b981;
  font-weight: 700;
  font-size: 11px;
}

.font-monospace {
  font-family: monospace;
}

.text-navy {
  color: #082c69 !important;
}

.text-danger {
  color: #ef4444 !important;
}
</style>
